import { Button, Card, Col, Input, Row, Statistic, Table } from 'antd';
import { DataTable, RangeDatePicker } from '~/components/common';
import {
  FaCalendarAlt,
  FaChartLine,
  FaCode,
  FaGift,
  FaHistory,
  FaSearch,
  FaTicketAlt,
  FaTrophy,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import React, { Fragment, useEffect, useReducer, useState } from 'react';
import { baseUrl } from '~/utils/helper';

import { HistoryGiftCodeGrid } from '~/data/dummy';
import InputAntd from './InputAntd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const reducer = (state, { action, payload }) => {
  switch (action) {
    case 'changeDate':
      return {
        ...state,
        startDate: payload.startDate + ' 00:00:00',
        endDate: payload.endDate + ' 23:59:59',
      };
    case 'username':
      return { ...state, username: payload };
    case 'page':
      return { ...state, page: payload };
    default:
      return state;
  }
};

const getHistoryGiftCode = async (params) => {
  try {
    const { data } = await axios.get(baseUrl + '/api/daily/giftcode/history', {
      params,
      headers: {
        'x-access-token': sessionStorage.getItem('auth'),
        'Access-Control-Allow-Origin': '*',
      },
    });
    if (data.status) {
      return data.data;
    }
    return {};
  } catch (error) {
    console.log(error);
  }
};

export default function HistoryGiftCode({ title }) {
  const { t } = useTranslation('report');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [params, dispatch] = useReducer(reducer, {
    startDate: dayjs().add(-7, 'd').format('YYYY-MM-DD') + ' 00:00:00',
    endDate: dayjs().format('YYYY-MM-DD') + ' 23:59:59',
    username: '',
    page: 1,
  });

  document.title = title;

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setLoading(true);
    const data = await getHistoryGiftCode(params);
    setData(data);
    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    const data = await getHistoryGiftCode({ ...params, page: 1 });
    setData(data);
    setLoading(false);
  };

  const handleChangeDate = (v) => {
    dispatch({
      action: 'changeDate',
      payload: { startDate: v[0], endDate: v[1] },
    });
  };

  const handleChangePage = (page) => {
    getHistoryGiftCode({ ...page, page: page });
    dispatch({ action: 'page', payload: page });
  };

  const historyGiftCodeGrid = HistoryGiftCodeGrid();

  return (
    <div className="min-h-screen p-2 md:p-6 bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
      {/* Header Section */}
      <div className="p-2 mb-4 border shadow-xl md:mb-8 md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <div className="flex flex-col justify-between gap-3 md:flex-row">
          {/* Title */}
          <div>
            <h1 className="flex items-center text-2xl font-bold text-center text-transparent bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text md:text-left md:text-3xl md:mb-2">
              <FaGift className="inline mr-3 text-purple-500" />
              {t('lich_su_giftcode')}
            </h1>
            <p className="hidden text-gray-600 md:block">{t('quan_ly_theo_doi_gift')}</p>
          </div>

          {/* Controls Row */}
          <div className="flex flex-col gap-2 lg:flex-row md:gap-4 lg:items-end">
            {/* Date Range - Hidden for now as commented in original */}
            {/* <div className="flex flex-col gap-2">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaCalendarAlt className="text-blue-500" />
                Khoảng thời gian
              </label>
              <RangeDatePicker
                onChange={handleChangeDate}
                className="w-full h-10 border-2 rounded-lg md:h-12 border-purple-500/20 bg-white/90"
              />
            </div> */}

            {/* Username Search */}
            <div className="flex justify-between gap-2 md:flex-col md:justify-center">
              <label className="items-center hidden gap-2 text-sm font-medium text-gray-700 md:flex">
                <FaUsers className="text-purple-500" />
                {t('nguoichoi')}
              </label>
              <Input
                value={params.username}
                onChange={(e) => dispatch({ action: 'username', payload: e.target.value })}
                placeholder={t('nhap_ten_dang_nhap')}
                className="md:w-[220px] w-3/5 rounded-lg md:h-12 h-10"
                allowClear
              />
              <button
                onClick={handleSearch}
                className="flex items-center justify-center h-10 gap-2 px-3 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:hidden md:h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:scale-105 hover:shadow-xl">
                <FaSearch size={16} />
                {t('common:search')}
              </button>
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="items-center justify-center hidden h-10 gap-2 px-6 py-3 font-semibold text-white transition-all duration-300 border-none shadow-lg md:flex md:h-12 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-600 hover:scale-105 hover:shadow-xl">
              <FaSearch size={16} />
              {t('common:search')}
            </button>
          </div>
        </div>
      </div>

      {/* Data Table Section */}
      <div className="p-2 border shadow-xl md:p-6 rounded-2xl bg-white/90 backdrop-blur-2xl border-white/20">
        <div className="custom-giftcode-table">
          <Table
            columns={historyGiftCodeGrid}
            loading={loading}
            dataSource={data?.data}
            sticky={true}
            pagination={{
              total: data?.total,
              showSizeChanger: false,
              onChange: (page) => handleChangePage(page),
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      <style jsx>{`
        .custom-giftcode-table .ant-table-thead > tr > th {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
          border-bottom: 2px solid #e2e8f0 !important;
          font-weight: 600 !important;
          color: #374151 !important;
          padding: 16px !important;
          text-align: center !important;
        }

        .custom-giftcode-table .ant-table-tbody > tr > td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }

        .custom-giftcode-table .ant-table-tbody > tr:nth-child(even) {
          background-color: #f9fafb !important;
        }

        .custom-giftcode-table .ant-table-tbody > tr:hover {
          background-color: #f3e8ff !important;
        }

        .ant-pagination .ant-pagination-item {
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
        }

        .ant-pagination .ant-pagination-item-active {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
          border-color: transparent !important;
        }

        .ant-pagination .ant-pagination-item-active a {
          color: white !important;
        }

        .ant-table-summary tr td {
          border-top: 2px solid #7c3aed !important;
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
          color: white !important;
          font-weight: bold !important;
        }

        /* Custom scrollbar */
        .custom-giftcode-table ::-webkit-scrollbar {
          height: 8px;
        }

        .custom-giftcode-table ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .custom-giftcode-table ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
          border-radius: 4px;
        }

        .custom-giftcode-table ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
        }

        /* Mobile responsive adjustments */
        @media (max-width: 768px) {
          .custom-giftcode-table .ant-table-thead > tr > th,
          .custom-giftcode-table .ant-table-tbody > tr > td {
            padding: 8px 12px !important;
            font-size: 14px !important;
          }
        }

        /* Enhanced button styling */
        button:hover {
          transform: translateY(-1px) !important;
        }

        /* Card animations */
        .ant-col .ant-card:hover {
          transform: scale(1.02) translateY(-2px) !important;
          box-shadow: 0 20px 40px rgba(139, 92, 246, 0.15) !important;
        }

        /* Input styling */
        .ant-input:focus {
          border-color: #8b5cf6 !important;
          box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1) !important;
        }

        /* Enhanced spacing */
        .space-y-4 > * + * {
          margin-top: 16px !important;
        }
      `}</style>
    </div>
  );
}
